import { type PlatformEventType, type SampleEvent } from "./TriggerRecord";

/** Roots every platform event exposes to templates: the envelope and the trigger itself. */
const RESERVED_ROOTS: readonly string[] = ["envelope", "trigger"];

const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

/** The kinds of value a template path resolves to. */
export type TemplatePathType =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "set"
  | "record";

/**
 * The data type a sample value states, or ``null`` for a value none of the
 * types describes (a list of numbers, a path the sample does not resolve).
 *
 * The samples carry no declared types, so this is the one place that guesses:
 * the variables panel, the condition builder, and the placeholder picker all
 * show the same answer.
 */
export function templateTypeOf(sample: unknown): TemplatePathType | null {
  if (typeof sample === "number") {
    return "number";
  }
  if (typeof sample === "boolean") {
    return "boolean";
  }
  if (typeof sample === "string") {
    return ISO_DATETIME.test(sample) ? "date" : "string";
  }
  // A null sample carries no type of its own, and every such field the
  // condition builder has met so far reads as text.
  if (sample === null) {
    return "string";
  }
  if (Array.isArray(sample)) {
    return sample.every((entry) => typeof entry === "string") ? "set" : null;
  }
  return isRecord(sample) ? "record" : null;
}

/** One ``root.path`` a template placeholder may name, with what it resolves to. */
export interface TemplatePath {
  /** Fully qualified, e.g. ``dataset.metadata.route``. */
  path: string;
  /** First segment of {@link path}. */
  root: string;
  /** Realistic value from the server's sample event, for previews. */
  sample: unknown;
  /** What the path resolves to; ``null`` when the sample states no type. */
  type: TemplatePathType | null;
  /**
   * True when the sample value is a nested record: the path is addressable
   * (a template renders it as JSON) but it also has children in the catalog.
   */
  container: boolean;
}

/**
 * Rank bucket for {@link TriggerTemplateCatalog.search}, best first: a prefix
 * of the whole path, a prefix of one segment, any substring.
 */
type MatchRank = 0 | 1 | 2;

function matchRank(path: string, query: string): MatchRank | null {
  if (path.startsWith(query)) {
    return 0;
  }
  if (path.includes(`.${query}`)) {
    return 1;
  }
  if (path.includes(query)) {
    return 2;
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Resolve a dotted path against a sample namespace. */
function valueAt(
  namespace: Record<string, Record<string, unknown>>,
  path: string,
): unknown {
  const [root, ...segments] = path.split(".");
  let current: unknown = namespace[root];
  for (const segment of segments) {
    if (!isRecord(current)) {
      return undefined;
    }
    current = current[segment];
  }
  return current;
}

/**
 * The vocabulary a trigger's target templates may draw on: every ``root.path``
 * that resolves on every event the trigger fires for, with a realistic sample
 * value for each.
 *
 * Built from the server's event samples (``GET /v1/triggers/events/samples``),
 * which the evaluator's own namespace produces, so a path offered here is one
 * a template will actually resolve at dispatch time. With several events
 * subscribed the vocabulary is their intersection, which is also what the
 * validator enforces at save time. The envelope (``envelope.*``) and the trigger
 * (``trigger.*``) are present on every sample, so they always survive.
 */
export class TriggerTemplateCatalog {
  private readonly byPath: ReadonlyMap<string, TemplatePath>;

  private constructor(
    private readonly entries: readonly TemplatePath[],
    /** False until the samples have been fetched: an empty catalog then means "not yet", not "nothing". */
    public readonly loaded: boolean,
  ) {
    this.byPath = new Map(entries.map((entry) => [entry.path, entry]));
  }

  /** A catalog with no vocabulary; ``loaded`` says whether that is final. */
  public static empty(loaded = false): TriggerTemplateCatalog {
    return new TriggerTemplateCatalog([], loaded);
  }

  /**
   * A catalog over a vocabulary the caller already has, in the order given.
   *
   * For a form whose placeholders are not event paths — an agent's own
   * variables, say — where there are no samples to intersect and the caller
   * is the authority on what exists.
   */
  public static fromPaths(
    entries: readonly TemplatePath[],
  ): TriggerTemplateCatalog {
    return new TriggerTemplateCatalog([...entries], true);
  }

  /**
   * The paths shared by every one of ``events``, in the first event's sample
   * order with the reserved roots moved last so entity paths lead the menu.
   */
  public static fromSamples(
    samples: Partial<Record<PlatformEventType, SampleEvent>>,
    events: readonly PlatformEventType[],
  ): TriggerTemplateCatalog {
    const present = events
      .map((eventType) => samples[eventType])
      .filter((sample): sample is SampleEvent => sample !== undefined);
    if (present.length === 0 || present.length !== events.length) {
      return TriggerTemplateCatalog.empty(true);
    }
    const [first, ...rest] = present;
    const shared = first.paths.filter((path) =>
      rest.every((sample) => sample.paths.includes(path)),
    );
    const toEntry = (path: string): TemplatePath => {
      const sample = valueAt(first.namespace, path);
      return {
        path,
        root: path.split(".", 1)[0],
        sample,
        type: templateTypeOf(sample),
        container: isRecord(sample),
      };
    };
    const entity = shared.filter(
      (path) => !RESERVED_ROOTS.includes(path.split(".", 1)[0]),
    );
    const reserved = shared.filter((path) =>
      RESERVED_ROOTS.includes(path.split(".", 1)[0]),
    );
    return new TriggerTemplateCatalog(
      [...entity, ...reserved].map(toEntry),
      true,
    );
  }

  public get paths(): readonly TemplatePath[] {
    return this.entries;
  }

  /** Distinct roots in path order. */
  public get roots(): readonly string[] {
    const seen: string[] = [];
    for (const entry of this.entries) {
      if (!seen.includes(entry.root)) {
        seen.push(entry.root);
      }
    }
    return seen;
  }

  public find(path: string): TemplatePath | undefined {
    return this.byPath.get(path);
  }

  public has(path: string): boolean {
    return this.byPath.has(path);
  }

  /** Paths under one root, in catalog order. */
  public under(root: string): TemplatePath[] {
    return this.entries.filter((entry) => entry.root === root);
  }

  /**
   * Case-insensitive match on the path text, ranked: a prefix of the whole
   * path first (``data`` → ``dataset.*``), then a prefix of a segment
   * (``na`` → ``dataset.name``), then any substring. Catalog order breaks ties.
   * An empty query returns everything.
   */
  public search(query: string): TemplatePath[] {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) {
      return [...this.entries];
    }
    const ranked: Array<{ entry: TemplatePath; rank: MatchRank }> = [];
    for (const entry of this.entries) {
      const rank = matchRank(entry.path.toLowerCase(), needle);
      if (rank !== null) {
        ranked.push({ entry, rank });
      }
    }
    return ranked.sort((a, b) => a.rank - b.rank).map(({ entry }) => entry);
  }
}
